import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Variant } from '~/api/Catalog';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductListAnalyticsContext } from '~/components/ProductListAnalytics/ProductListAnalyticsContext';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useModal } from '~/hooks/useModal';
import { useMounted } from '~/hooks/useMounted';
import { useProductLimit } from '~/hooks/useProductLimit';
import { useVibration } from '~/hooks/useVibration';
import { toAddToCartSource } from '~/services/analytics/productLists';
import { cartStore, catalogStore, orderStore } from '~/stores';
import { type Offer, Product } from '~/stores/CategoriesStore';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import { isProductBundle } from '~/types/Product';
import { getLowestMultibuyLevel } from '~/utils/multibuy';

export const useProductCard = (
  product: Product,
  currentOffer?: Offer | null,
) => {
  const mounted = useMounted();
  const productList = useProductListAnalyticsContext();
  const [offer, setOffer] = useState(currentOffer ?? product.defaultOffer);

  useEffect(() => {
    if (currentOffer) {
      setOffer(currentOffer);
    }
  }, [currentOffer]);

  const isFavorite = mounted && favoritesStore.isFavorited(product.id);

  const isBundle = isProductBundle(product);
  const { openModal } = useModal();

  const currentItemWithoutProperties = isBundle ? product : offer || product;
  const propertyFromVariant = product.variants?.find(
    ({ sku }) => sku === currentItemWithoutProperties.sku,
  );
  const propertiesFromVariant = {};
  if (propertyFromVariant) {
    propertiesFromVariant[`${propertyFromVariant.property}`] = {
      ...currentItemWithoutProperties.properties[propertyFromVariant.property],
      value: propertyFromVariant.value,
    };
  }
  if (Object.keys(propertiesFromVariant).length > 0) {
    // temporarily substitute properties if needed, but keep class instance
    // Use Object.defineProperty to override properties field if needed, non-intrusively
    Object.defineProperty(currentItemWithoutProperties, 'properties', {
      value: {
        ...currentItemWithoutProperties.properties,
        ...propertiesFromVariant,
      },
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  const currentItem = currentItemWithoutProperties;

  const [image, setImage] = useState(currentItem.previewImageThumb);
  const { limit, currentWhSellable, parentWhSellable } = useProductLimit({
    offer: currentItem,
  });

  const currentPrice = currentItem.discountPrice
    ? currentItem.discountPriceFormatted
    : currentItem.priceFormatted;
  const oldPrice = currentItem.discountPrice
    ? currentItem.priceFormatted
    : null;
  const cartItemsCount = mounted
    ? cartStore.getCartItemCountById(currentItem)
    : 0;

  const isExpress =
    company.showExpressBadge &&
    currentItem &&
    currentWhSellable &&
    currentWhSellable >= cartItemsCount &&
    orderStore.isExpressAvailableNow;
  const isBackorderAvailable =
    currentItem &&
    currentItem.isBackorderAvailable &&
    !!currentItem.backorderLeadTime &&
    parentWhSellable > 0;
  const lowestLevel = !isBundle
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;
  const isMultiBuy = !isBundle && Boolean(lowestLevel);

  const { vibrateOnChange } = useCartVibration({
    isMultibuy: isMultiBuy,
    requiredQuantity: lowestLevel?.quantity ?? 0,
    currentCount: cartItemsCount,
  });

  const leadTime = currentItem.backorderLeadTime
    ? currentItem.backorderLeadTime
    : 0;

  const productHasVariants = useMemo(() => {
    return product.variants?.length
      ? product.variants.map(({ sku, value }) => ({ id: sku, value }))
      : product.offers.map((item) => ({
          id: item.sku,
          value: formatVolume(item.properties),
        }));
  }, [product.variants, product.offers]).length;

  const productHasManyVariants = productHasVariants > 1;

  const colorVariants = useMemo(
    () =>
      Object.values(
        product.variants?.reduce((acc: Record<string, Variant>, item) => {
          if (item.property === 'color' && !acc[item.value]) {
            acc[item.value] = item;
          }
          return acc;
        }, {}) || {},
      ).sort((a, b) => a.sorting - b.sorting),
    [product.variants],
  );

  const volumes = useMemo(() => {
    return product.variants?.length
      ? product.variants
          .filter(({ property }) => property === 'volume')
          .map(({ sku, value }) => ({ id: sku, value }))
      : product.offers.map((item) => ({
          id: item.sku,
          value: formatVolume(item.properties),
        }));
  }, [product.variants, product.offers]);

  const selectOffer = useCallback(
    (sku: string) => {
      const newOffer = product.offers.find((item) => item.sku === sku);

      if (newOffer) {
        setImage(newOffer.previewImageThumb);
        return setOffer(newOffer);
      }

      const variant = product.variants?.find((variant) => variant.sku === sku);

      if (!variant) {
        return;
      }

      const { thumb, url } = (variant.images || []).reduce<{
        thumb: string;
        url: string;
      }>(
        (acc, { thumb, url }) => {
          if (thumb && !acc.thumb) {
            acc.thumb = thumb;
          }

          if (url && !acc.url) {
            acc.url = url;
          }

          return acc;
        },
        { thumb: '', url: '' },
      );

      if (thumb || url) {
        setImage(thumb || url);
      }

      Product.loadOffer(product, sku).then((offersMap) => {
        const newOffer = offersMap.get(sku);
        if (newOffer) {
          setImage(newOffer.previewImageThumb);
          setOffer(newOffer);
        }
      });
    },
    [product],
  );

  const { vibrate: vibrateFavorite } = useVibration({ type: 'soft' });

  const toggleFavorite = useCallback(() => {
    favoritesStore.toggleFavorite(product, 'product_main');
    vibrateFavorite();
  }, [product, vibrateFavorite]);

  const changeCount = useCallback(
    (count: number, action: 'add' | 'remove') => {
      vibrateOnChange(count, action);

      cartStore.setCartItemCountByProduct(
        {
          ...currentItem,
          variants: product.variants,
          ratingAverage: currentItem?.ratingAverage ?? 0,
          ratingMarksCount: currentItem?.ratingMarksCount ?? 0,
        },
        count,
        action,
        toAddToCartSource(productList),
      );
    },
    [currentItem, product, productList, vibrateOnChange],
  );

  const addMultiBuyProductToCart = useCallback(() => {
    if (!lowestLevel) {
      return;
    }

    const targetCount = lowestLevel.quantity - cartItemsCount;

    if (targetCount <= 0 || limit < cartItemsCount + targetCount) {
      return;
    }

    changeCount(cartItemsCount + targetCount, 'add');
  }, [lowestLevel, changeCount, cartItemsCount, limit]);

  const openSelectSKUModal = useCallback(() => {
    catalogStore.setSelectedCatalogProduct(
      product.slug,
      toAddToCartSource(productList),
    );
    openModal(ModalType.SelectSKU);
  }, [product, productList]);

  useEffect(() => {
    if (product) {
      setOffer(product.defaultOffer);
      setImage(currentItem.previewImageThumb);
    }
  }, [product]);

  return {
    currentItem,
    offerSku: offer?.sku ?? product.presentedVariantSku ?? '',
    volumes,
    limit,
    image,
    leadTime,
    oldPrice,
    currentPrice,
    cartItemsCount,
    isMultiBuy,
    isExpress,
    isBackorderAvailable,
    isBundle,
    isFavorite,
    productHasVariants,
    productHasManyVariants,
    colorVariants: [...new Set(colorVariants)],
    selectOffer,
    toggleFavorite,
    changeCount,
    addMultiBuyProductToCart,
    openSelectSKUModal,
  };
};
